import React, { useEffect, useRef } from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { LoadingSpinner } from "../../../common/LoadingSpinner";
import {
    IFormUUID,
    IProductUUID,
    IReviewsProductTypeID,
} from "../../../models/nominals";
import {
    IReviewQuery,
    IReviewsProduct,
    IReviewsProductVariant,
    ISearchFacet,
    ISearchReviewArgs,
    ReviewSortOption,
} from "../../../models/reviews";
import { UGCListSubBlock } from "../../../models/ugc";
import { notEmpty, unique } from "../../../utils/functional";
import { serializeFacetValues } from "../../../utils/reviews";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { ProductReviews as ProductReviewsComponent } from "../components/ProductReviews";
import { ReviewsComponentTypes, WriteReviewFormMode } from "../constants";
import { defaultState } from "../defaults";
import {
    registerNewWriteReviewForm,
    setFacetValues,
    setProductTypes,
    updateSorting,
} from "../reducers";
import { getWriteReviewFormSelectedProduct } from "../selectors";
import {
    loadProductsByUUIDs,
    loadReviewTemplate,
    loadReviews,
} from "../thunks";
import type { IReviewsAppOwnProps } from "./_base";

interface IProps extends IReviewsAppOwnProps {
    title: string;
    productUUIDs: IProductUUID[];
    numReviews: number;
    averageRating: number;
    showWriteReviewButton?: boolean;
    writeReviewFormUUID: IFormUUID;
    ugcCarouselDesc?: string;
    ugcCarouselPosts?: UGCListSubBlock[];
    brand?: string;
    showProductsFilter?: boolean;
    showSourcesFilter?: boolean;
    reviewsComponentType?: ReviewsComponentTypes;
    isCollapsedView?: boolean;
}

const getDefaultFilterOptions = (products: ReadonlyArray<IReviewsProduct>) => {
    // Select all the products types
    const productTypeIDs = products
        .map((p) => {
            return p.product_type;
        })
        .filter(notEmpty)
        .filter(unique);
    // Select all the products
    const productIDs = products
        .map((p) => p.id)
        .filter(notEmpty)
        .filter(unique);
    // Select all the product variants
    const variantIDs = products
        .reduce<IReviewsProductVariant[]>(
            (memo, p) => memo.concat(p.variants),
            [],
        )
        .map((v) => v.id)
        .filter(notEmpty)
        .filter(unique);
    return {
        productTypeIDs,
        productIDs,
        variantIDs,
    };
};

const buildReviewSearchQuery = (args: {
    brandID: IProps["brandID"];
    pageSize: IProps["pageSize"];
    displayedProductTypeIDWhitelist: IProps["displayedProductTypeIDWhitelist"];
    productTypeIDs: readonly IReviewsProductTypeID[];
    selectedSortOption: ReviewSortOption;
    facetValues: IReviewQuery;
    page: number;
    products: ReadonlyArray<IReviewsProduct>;
}): ISearchReviewArgs => {
    let productTypeIDs = args.productTypeIDs;
    if (
        productTypeIDs.length <= 0 &&
        args.displayedProductTypeIDWhitelist &&
        args.displayedProductTypeIDWhitelist.length > 0
    ) {
        productTypeIDs = args.displayedProductTypeIDWhitelist;
    }
    const productTypeIDString = serializeFacetValues(productTypeIDs);

    // Add product/variant filtering
    const {
        productTypeIDs: defaultProductTypeIDs,
        productIDs,
        variantIDs,
    } = getDefaultFilterOptions(args.products);

    // Remove product_type_id from facetValues to avoid type conflicts
    const { product_type_id: _, ...facetValuesWithoutProductType } =
        args.facetValues;

    const query: ISearchReviewArgs = {
        brand_id: args.brandID,
        ordering: args.selectedSortOption,
        product_type_id: productTypeIDString ? productTypeIDString : undefined,
        page: args.page,
        page_size: args.pageSize,
        ...facetValuesWithoutProductType,
    };

    // Override product_type_id if not set
    if (!query.product_type_id) {
        const defaultProductTypeIDString = serializeFacetValues(
            defaultProductTypeIDs,
        );
        query.product_type_id = defaultProductTypeIDString
            ? defaultProductTypeIDString
            : undefined;
    }
    // Override product_id and product_variant_id if not set
    if (!query.product_id && !query.product_variant_id) {
        const productIDString = serializeFacetValues(productIDs);
        const variantIDString = serializeFacetValues(variantIDs);
        query.product_id = productIDString ? productIDString : undefined;
        query.product_variant_id = variantIDString
            ? variantIDString
            : undefined;
    }
    return query;
};

const listReviewsAsync = async (
    args: Parameters<typeof buildReviewSearchQuery>[0] & {
        dispatch: ReturnType<typeof useAppDispatch>;
        appendToExisting: boolean;
    },
): Promise<void> => {
    const query = buildReviewSearchQuery(args);
    await args.dispatch(
        loadReviews({
            brandID: args.brandID,
            query,
        }),
    );
};

const listProductsAsync = async (
    productUUIDs: IProductUUID[],
    writeReviewFormUUID: IFormUUID,
    dispatch: ReturnType<typeof useAppDispatch>,
    facetValues: IReviewQuery,
): Promise<void> => {
    // Load products for the given UUIDs
    const result = await dispatch(loadProductsByUUIDs(productUUIDs));
    if (!loadProductsByUUIDs.fulfilled.match(result)) {
        return;
    }
    const products = result.payload;

    // Load Review Template for first product
    if (products.length > 0) {
        await dispatch(
            loadReviewTemplate({
                formUUID: writeReviewFormUUID,
                productID: products[0].id,
            }),
        );
    }
    // Set default product type selections
    const { productTypeIDs, productIDs, variantIDs } =
        getDefaultFilterOptions(products);
    dispatch(setProductTypes(productTypeIDs));
    // Set default filter selections
    dispatch(
        setFacetValues({
            ...facetValues,
            product_id: serializeFacetValues(productIDs),
            product_variant_id: serializeFacetValues(variantIDs),
        }),
    );
};

const initSourceFilter = (args: {
    sourceFilterWhitelist: IProps["sourceFilterWhitelist"];
    facetValues: IReviewQuery;
    facets: ReadonlyArray<ISearchFacet>;
    dispatch: ReturnType<typeof useAppDispatch>;
}): void => {
    const sourceFilterWhitelist = args.sourceFilterWhitelist;
    if (!sourceFilterWhitelist || sourceFilterWhitelist.length <= 0) {
        return;
    }
    const sourceFacet = args.facets.find((facet) => {
        return facet.type === "source_id";
    });
    if (!sourceFacet) {
        return;
    }
    const optionIDs = sourceFacet.options
        .filter((option): boolean => {
            return sourceFilterWhitelist.includes(`${option.name}`);
        })
        .map((option): number => {
            return option.option_id;
        });
    const sourceIDs = serializeFacetValues(optionIDs);
    if (args.facetValues.source_id !== sourceIDs) {
        args.dispatch(
            setFacetValues({
                ...args.facetValues,
                source_id: sourceIDs,
            }),
        );
    }
};

export const ProductReviews = (props: IProps): React.ReactNode => {
    const {
        brandID,
        defaultSortOption = ReviewSortOption.HIGHEST_RATED,
        pageSize = 6,
        displayedProductTypeIDWhitelist,
        sourceFilterWhitelist,
        starHasStroke,
        title,
        productUUIDs,
        numReviews,
        averageRating,
        showWriteReviewButton = true,
        writeReviewFormUUID,
        ugcCarouselDesc = "",
        ugcCarouselPosts = [],
        brand = "Tempur-Pedic",
        showProductsFilter = false,
        showSourcesFilter = false,
        reviewsComponentType,
        isCollapsedView,
    } = props;

    const state = useAppSelector(
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (rootState) => rootState.reviews || defaultState,
    );
    const dispatch = useAppDispatch();

    const writeReviewSelectedProduct = getWriteReviewFormSelectedProduct(
        state,
        {
            formUUID: writeReviewFormUUID,
        },
    );

    const listReviewsTimerRef = useRef<number | undefined>(undefined);

    const listReviewsDebounced = (
        page: number,
        appendToExisting: boolean,
        delayMs = 100,
    ) => {
        const window = getWindow();
        if (!window) {
            return;
        }
        if (listReviewsTimerRef.current) {
            window.clearTimeout(listReviewsTimerRef.current);
        }
        listReviewsTimerRef.current = window.setTimeout(() => {
            void listReviewsAsync({
                brandID,
                pageSize,
                displayedProductTypeIDWhitelist,
                productTypeIDs: state.ui.productTypeIDs,
                selectedSortOption: state.ui.selectedSortOption,
                facetValues: state.ui.facetValues,
                page,
                dispatch,
                appendToExisting,
                products: state.data.products,
            });
        }, delayMs);
    };

    const onLoadMore = async (): Promise<void> => {
        await listReviewsAsync({
            brandID,
            pageSize,
            displayedProductTypeIDWhitelist,
            productTypeIDs: state.ui.productTypeIDs,
            selectedSortOption: state.ui.selectedSortOption,
            facetValues: state.ui.facetValues,
            page: state.ui.page + 1,
            dispatch,
            appendToExisting: true,
            products: state.data.products,
        });
    };

    // Initial mount: register form, load products, set default sort, and load reviews
    useEffect(() => {
        dispatch(
            registerNewWriteReviewForm({
                formUUID: writeReviewFormUUID,
                mode: WriteReviewFormMode.CLOSED,
            }),
        );
        initSourceFilter({
            sourceFilterWhitelist,
            facetValues: state.ui.facetValues,
            facets: state.data.facets,
            dispatch,
        });
        void listProductsAsync(
            productUUIDs,
            writeReviewFormUUID,
            dispatch,
            state.ui.facetValues,
        );
        dispatch(updateSorting(defaultSortOption));
        listReviewsDebounced(1, false);
        // Only run on mount
    }, []);

    // Update source filter when sourceFilterWhitelist or facets change
    useEffect(() => {
        initSourceFilter({
            sourceFilterWhitelist,
            facetValues: state.ui.facetValues,
            facets: state.data.facets,
            dispatch,
        });
    }, [
        sourceFilterWhitelist,
        state.data.facets,
        state.ui.facetValues,
        dispatch,
    ]);

    // Reload reviews when sort, filters, or product types change
    useEffect(() => {
        listReviewsDebounced(1, false);
    }, [
        state.ui.selectedSortOption,
        state.ui.productTypeIDs,
        state.ui.facetValues,
    ]);

    if (!state.data.loadedInitialProducts || !state.data.loadedInitialReviews) {
        return <LoadingSpinner />;
    }

    const writeReviewFormTitle = writeReviewSelectedProduct
        ? `Write a Review for ${writeReviewSelectedProduct.name}`
        : `Write a Review`;

    return (
        <ProductReviewsComponent
            title={title}
            numReviews={numReviews}
            averageRating={averageRating}
            showWriteReviewButton={!!showWriteReviewButton}
            displayedProductTypeIDWhitelist={[]}
            onLoadMore={onLoadMore}
            ugcCarouselDesc={ugcCarouselDesc}
            ugcCarouselPosts={ugcCarouselPosts}
            brand={brand}
            starHasStroke={starHasStroke}
            showProductsFilter={!!showProductsFilter}
            showSourcesFilter={!!showSourcesFilter}
            writeReviewFormUUID={writeReviewFormUUID}
            writeReviewFormTitle={writeReviewFormTitle}
            reviewsComponentType={reviewsComponentType}
            isCollapsedView={isCollapsedView}
        />
    );
};
